/* istanbul ignore file */
import { useIntl, defineMessages } from 'react-intl';
import {
  Box,
  Checkbox,
  Divider,
  Stack,
  Typography,
  Image,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  InlineNotification,
} from '@packages/shared';
import { useSession } from '@packages/utilities';
import { mutate } from 'swr';
import { useState, useEffect, useRef } from 'react';
import { ArrowMediumDown32, Checkmark32 } from '@packages/themes/icons';
import type { ServiceItem } from '../AddedProduct/useBasketData';
import { useToggleServiceProduct } from './useToggleServiceProduct';
import { useFormatedPrice } from '../../../../hooks/useFormatedPrice/useFormatedPrice';

export const messages = defineMessages({
  chipLabel: { id: 'atblayer.chipLabel', defaultMessage: 'TIPP' },
  serviceItemsHeadline: {
    id: 'atblayer.serviceitems.card.headline',
    defaultMessage: 'Einfach mitbestellen',
  },
  serviceItemsMoreInfo: {
    id: 'atblayer.serviceitems.more.text',
    defaultMessage: 'Mehr Infos',
  },
  serviceItemsInclusive: {
    id: 'atblayer.serviceitems.inclusive',
    defaultMessage: 'Inklusive',
  },
  serviceItemsErrorMessage: {
    id: 'atblayer.serviceitems.error.message',
    defaultMessage: 'Änderung konnte nicht gespeichert werden',
  },
});

// TODO(api-layer): needs to be refactored with gql fragments when basket resolvers are implemented
export const ServiceItems = ({
  serviceItems,
  pli,
}: {
  serviceItems: Array<ServiceItem>;
  pli: string;
}) => {
  const { formatMessage } = useIntl();
  const formatPrice = useFormatedPrice('ATB');
  const { jwtToken } = useSession();
  const [loadingItem, setLoadingItem] = useState<string | null>(null);
  const [errorNotification, setErrorNotification] = useState(false);
  const refTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { trigger } = useToggleServiceProduct();

  const onSelectItem = async (sku: string) => {
    try {
      setLoadingItem(sku);
      await trigger({ pli, sku });
      await mutate(jwtToken ? ['/api/checkout/v1/', jwtToken, pli] : null);
    } catch {
      setErrorNotification(true);
      refTimer.current = setTimeout(() => {
        setErrorNotification(false);
      }, 5000);
    } finally {
      setLoadingItem(null);
    }
  };

  useEffect(
    () => () => {
      if (refTimer.current !== null) {
        window.clearTimeout(refTimer.current);
      }
    },
    [],
  );

  return (
    <>
      {errorNotification && (
        <InlineNotification severity="error">
          {formatMessage(messages.serviceItemsErrorMessage)}
        </InlineNotification>
      )}
      <Accordion
        defaultExpanded
        sx={{ boxShadow: 0, '&.Mui-expanded': { ':before': { opacity: 1 } } }}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<ArrowMediumDown32 />}
          aria-controls="serviceitems-content"
          id="serviceitems-header"
          sx={{ p: 0 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              sx={{
                display: 'flex',
                color: 'common.white',
                height: 16,
                typography: 'body3',
                textTransform: 'uppercase',
              }}
              label={formatMessage(messages.chipLabel)}
              size="small"
              color="success"
            />
            <Typography variant="body2">{formatMessage(messages.serviceItemsHeadline)}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={1} divider={<Divider />}>
            {serviceItems.map(({ label, services }) => (
              <Stack key={label} gap={2}>
                <Typography
                  variant="body3"
                  fontWeight="fontWeightSemiBold"
                  sx={{ textTransform: 'uppercase' }}
                >
                  {label}
                </Typography>
                {services.map(({ sku, name, price, isBooked, isIncluded, iconUrl }) => (
                  <Stack key={sku} direction="row" alignItems="flex-start">
                    <Box
                      sx={{ width: '2.5rem', height: '1.75rem' }}
                      display="flex"
                      alignItems="center"
                    >
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isIncluded ? (
                        <Checkmark32 fontSize="medium" color="success" />
                      ) : loadingItem === sku ? (
                        <CircularProgress size="s" />
                      ) : (
                        <Checkbox
                          sx={{ padding: 0 }}
                          checked={isBooked}
                          onChange={() => onSelectItem(sku)}
                        />
                      )}
                    </Box>
                    <Stack
                      direction="row"
                      gap={2}
                      alignItems="flex-start"
                      flex={1}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Box>
                        <Image src={`${iconUrl}.svg`} alt={name!} width={30} height={30} />
                      </Box>
                      <Stack flex={1}>
                        <Stack direction="row" justifyContent="space-between" gap={1}>
                          <Typography data-testid="service-name" variant="body2">
                            {name}
                          </Typography>
                          {isIncluded ? (
                            <Typography
                              variant="body2"
                              fontWeight="fontWeightSemiBold"
                              sx={{ color: 'success.main' }}
                            >
                              {formatMessage(messages.serviceItemsInclusive)}
                            </Typography>
                          ) : (
                            <Typography
                              variant="body2"
                              fontWeight="fontWeightSemiBold"
                              whiteSpace="nowrap"
                            >
                              +{formatPrice(price.value, price.currencyCode)}
                            </Typography>
                          )}
                        </Stack>
                        <Typography variant="body3" sx={{ color: 'text.darkTransparent' }}>
                          {formatMessage(messages.serviceItemsMoreInfo)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
